// Web Bluetooth link to a hub running the official Pybricks firmware (Pybricks BLE profile v1.2+).
export const PYBRICKS_SERVICE = "c5f50001-8280-46da-89f4-6d8051e4aeef";
const CMD_EVENT_CHAR = "c5f50002-8280-46da-89f4-6d8051e4aeef";
const CAPABILITIES_CHAR = "c5f50003-8280-46da-89f4-6d8051e4aeef";
const DEVICE_INFO = "device_information";

const CMD = { STOP: 0, START: 1, WRITE_META: 3, WRITE_RAM: 4 };
const EVT = { STATUS: 0, STDOUT: 1 };
const FLAG = { LOW_BATTERY: 1 << 0, PROGRAM_RUNNING: 1 << 6 };
const MAX_RECONNECTS = 6;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const withTimeout = (promise, ms, msg) =>
  Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);

const u32Message = (cmd, value) => {
  const msg = new Uint8Array(5);
  msg[0] = cmd;
  new DataView(msg.buffer).setUint32(1, value, true);
  return msg;
};

export const INITIAL_HUB_STATE = {
  status: "disconnected",
  name: "",
  firmware: "",
  profile: "",
  maxWrite: 0,
  maxProgram: 0,
  running: false,
  lowBattery: false,
  progress: null,
  reconnectAttempt: 0,
};

export class HubConnection {
  constructor({ onUpdate, onStdout, onLog }) {
    this.state = { ...INITIAL_HUB_STATE };
    this.onUpdate = onUpdate;
    this.onStdout = onStdout;
    this.onLog = onLog;
    this.device = null;
    this.cmd = null;
    this.wanted = false;
    this.reconnecting = false;
    this.queue = Promise.resolve();
    this.decoder = new TextDecoder();
  }

  set(patch) {
    this.state = { ...this.state, ...patch };
    this.onUpdate(this.state);
  }

  // GATT allows one operation at a time; queue every write so nothing collides.
  write(bytes) {
    const p = this.queue.then(() => {
      if (!this.cmd) throw new Error("Hub is not connected");
      return this.cmd.writeValueWithResponse(bytes);
    });
    this.queue = p.catch(() => {});
    return p;
  }

  async connect() {
    if (!navigator.bluetooth) throw new Error("This browser can't use Bluetooth. Open HubPilot in Chrome or Edge.");
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [PYBRICKS_SERVICE] }],
      optionalServices: [DEVICE_INFO],
    });
    if (this.device && this.device !== device) await this.disconnect();
    this.device = device;
    device.removeEventListener("gattserverdisconnected", this.handleDisconnect);
    device.addEventListener("gattserverdisconnected", this.handleDisconnect);
    this.wanted = true;
    this.set({ status: "connecting", name: device.name || "Hub" });
    try {
      await this.openGatt();
    } catch (e) {
      this.wanted = false;
      this.set({ status: "disconnected" });
      throw e;
    }
  }

  async openGatt() {
    const server = await withTimeout(this.device.gatt.connect(), 12000, "The hub did not answer. Is it switched on and close by?");
    const service = await server.getPrimaryService(PYBRICKS_SERVICE);
    const cmd = await service.getCharacteristic(CMD_EVENT_CHAR);
    let caps = { maxWrite: 0, maxProgram: 0 };
    try {
      const value = await (await service.getCharacteristic(CAPABILITIES_CHAR)).readValue();
      caps = { maxWrite: value.getUint16(0, true), maxProgram: value.getUint32(6, true) };
    } catch {
      this.log("error", "This hub's firmware is too old for HubPilot. Install the latest Pybricks firmware.");
    }
    let firmware = "";
    let profile = "";
    try {
      const info = await server.getPrimaryService(DEVICE_INFO);
      const dec = async (c) => this.decoder.decode(await (await info.getCharacteristic(c)).readValue());
      firmware = await dec("firmware_revision_string");
      profile = await dec("software_revision_string");
    } catch {
      // Device information is optional for running programs.
    }
    cmd.removeEventListener("characteristicvaluechanged", this.handleNotify);
    cmd.addEventListener("characteristicvaluechanged", this.handleNotify);
    await cmd.startNotifications();
    this.cmd = cmd;
    this.queue = Promise.resolve();
    this.set({ status: "connected", name: this.device.name || "Hub", firmware, profile, ...caps, reconnectAttempt: 0 });
  }

  handleNotify = (event) => {
    const v = event.target.value;
    if (v.byteLength < 1) return;
    const type = v.getUint8(0);
    if (type === EVT.STATUS && v.byteLength >= 5) {
      const flags = v.getUint32(1, true);
      this.set({ running: Boolean(flags & FLAG.PROGRAM_RUNNING), lowBattery: Boolean(flags & FLAG.LOW_BATTERY) });
    } else if (type === EVT.STDOUT) {
      this.onStdout(this.decoder.decode(new Uint8Array(v.buffer, v.byteOffset + 1, v.byteLength - 1), { stream: true }));
    }
  };

  handleDisconnect = async () => {
    this.cmd = null;
    this.set({ running: false, progress: null });
    if (!this.wanted) {
      this.set({ status: "disconnected" });
      return;
    }
    if (this.reconnecting) return;
    this.reconnecting = true;
    this.log("info", "Bluetooth link to the hub dropped. Reconnecting…");
    for (let attempt = 1; attempt <= MAX_RECONNECTS && this.wanted; attempt++) {
      this.set({ status: "reconnecting", reconnectAttempt: attempt });
      await sleep(Math.min(500 * 2 ** (attempt - 1), 5000));
      if (!this.wanted) break;
      try {
        await this.openGatt();
        this.reconnecting = false;
        this.log("info", `Reconnected to ${this.state.name}.`);
        return;
      } catch {
        // try again with a longer pause
      }
    }
    this.reconnecting = false;
    if (this.wanted) this.log("error", "Could not reconnect. Check the hub is on, then press Connect hub.");
    this.wanted = false;
    this.set({ status: "disconnected", reconnectAttempt: 0 });
  };

  async disconnect() {
    this.wanted = false;
    if (this.device?.gatt?.connected) this.device.gatt.disconnect();
    this.cmd = null;
    this.set({ status: "disconnected", running: false, progress: null, reconnectAttempt: 0 });
  }

  async stop() {
    await this.write(new Uint8Array([CMD.STOP]));
  }

  async waitUntilStopped(ms) {
    const end = Date.now() + ms;
    while (this.state.running && Date.now() < end) await sleep(50);
  }

  // Pybricks multi-mpy6 format: [u32 size][module name\0][mpy bytes]
  static packProgram(mpy) {
    const name = new TextEncoder().encode("__main__\0");
    const blob = new Uint8Array(4 + name.length + mpy.length);
    new DataView(blob.buffer).setUint32(0, mpy.length, true);
    blob.set(name, 4);
    blob.set(mpy, 4 + name.length);
    return blob;
  }

  async downloadAndRun(mpy) {
    if (!this.cmd) throw new Error("Connect a hub first.");
    if (!this.state.maxWrite) throw new Error("This hub's firmware is too old. Install the latest Pybricks firmware.");
    const blob = HubConnection.packProgram(mpy);
    if (this.state.maxProgram && blob.length > this.state.maxProgram) throw new Error("This setup is too big for the hub.");
    if (this.state.running) {
      await this.stop();
      await this.waitUntilStopped(3000);
    }
    this.set({ progress: 0 });
    try {
      await this.write(u32Message(CMD.WRITE_META, 0));
      const chunk = this.state.maxWrite - 5;
      for (let offset = 0; offset < blob.length; offset += chunk) {
        const part = blob.subarray(offset, offset + chunk);
        const msg = new Uint8Array(5 + part.length);
        msg[0] = CMD.WRITE_RAM;
        new DataView(msg.buffer).setUint32(1, offset, true);
        msg.set(part, 5);
        await this.write(msg);
        this.set({ progress: Math.min(1, (offset + part.length) / blob.length) });
      }
      await this.write(u32Message(CMD.WRITE_META, blob.length));
      await this.write(new Uint8Array([CMD.START]));
    } finally {
      this.set({ progress: null });
    }
  }

  log(kind, text) {
    this.onLog(kind, text);
  }
}
